import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  Checkbox,
  EmptyState,
  Grid,
  GridColumn,
  GridRow,
  ProfileIcon,
  SearchInput,
} from '@bp/ui-components';
import classNames from 'classnames';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { naturalStringSort } from 'utils/stringHelper';
import { uniqueByUuid } from 'utils/uniquByUuid';
import styles from './ProfileSelection.module.scss';

export const ProfileSelectionFragment = graphql(`
  fragment ProfileSelectionFields on Query {
    profiles(where: { organization: { uuid: $organizationUuid } }, options: { sort: { selectName: ASC } }) {
      __typename
      uuid
      selectName
      organizationRoles
    }
    classes(where: { organization: { uuid: $organizationUuid } }, options: { sort: { name: ASC } }) {
      __typename
      uuid
      name
      schoolYear {
        __typename
        uuid
      }
      membersConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            uuid
            displayName
          }
        }
      }
      divisionsConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            uuid
          }
        }
      }
    }
    classGroups(where: { organization: { uuid: $organizationUuid } }, options: { sort: { name: ASC } }) {
      __typename
      uuid
      name
      division {
        __typename
        class {
          __typename
          schoolYear {
            __typename
            uuid
          }
        }
      }
      membersConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            uuid
          }
        }
      }
      division {
        __typename
        uuid
      }
    }
  }
`);

export type SelectableMember = {
  uuid: string;
  name: string;
  organizationRoles: string[];
  selected: boolean;
};

export type GroupedProfile = {
  classOrClassGroupUuid: string;
  name: string;
  collapsed: boolean;
  members: SelectableMember[];
};

type ProfileSelectionProps = {
  context?: string;
  initialMembers: SelectableMember[];
  excludeMemberUuids?: string[];
  schoolYearUuid: string;
  categoriesOnly?: boolean;
  onPick: (pickedMembers: SelectableMember[]) => void;
  query: FragmentType<typeof ProfileSelectionFragment> | undefined;
};

export const ProfileSelection = ({
  context = 'selection',
  initialMembers,
  excludeMemberUuids = [],
  schoolYearUuid,
  categoriesOnly,
  onPick,
  query,
}: ProfileSelectionProps) => {
  const { t } = useTranslation();

  const fragment = getFragmentData(ProfileSelectionFragment, query);
  const profiles = fragment?.profiles ?? [];
  const classes = fragment?.classes.filter((c) => c.schoolYear.uuid === schoolYearUuid) ?? [];
  const classGroups = fragment?.classGroups.filter((c) => c.division.class.schoolYear.uuid === schoolYearUuid) ?? [];

  const students = profiles.filter((p) => p.organizationRoles.includes('STUDENT'));
  const teachers = profiles.filter((p) => p.organizationRoles.includes('TEACHER'));
  const parents = profiles.filter((p) => p.organizationRoles.includes('PARENT'));
  const others = profiles.filter((p) => p.organizationRoles.includes('OTHER'));

  const uniqueMembers = useMemo(() => uniqueByUuid(initialMembers), [initialMembers]);

  const [availableSearch, setAvailableSearch] = useState<string>('');
  const [pickedSearch, setPickedSearch] = useState<string>('');

  const [available, setAvailable] = useState<GroupedProfile[]>([]);
  const [picked, setPicked] = useState<GroupedProfile[]>([]);

  // AVAILABLE PROFILES
  const filteredAvailable = available
    // apply search
    .map((item) => ({
      ...item,
      members: item.members.filter((member) => member.name.toLowerCase().includes(availableSearch)),
    }))
    // sort, ignore 'special' categories
    .sort((a, b) => {
      const aIsCategory = a.classOrClassGroupUuid.startsWith('!');
      const bIsCategory = b.classOrClassGroupUuid.startsWith('!');
      if (aIsCategory && !bIsCategory) return 1;
      if (!aIsCategory && bIsCategory) return -1;
      return naturalStringSort(a.name, b.name);
    })
    // remove empty classes/classGroups
    .filter((g) => g.members.length > 0);

  const availableCount = uniqueByUuid(filteredAvailable.flatMap((p) => p.members)).length;
  const availableSelectedCount = uniqueByUuid(
    filteredAvailable.flatMap((p) => p.members.filter((m) => m.selected)),
  ).length;
  const allAvailableSelected = availableCount > 0 && availableCount === availableSelectedCount;

  function handleToggleAvailableAll() {
    setAvailable((prev) =>
      prev.map((group) => {
        const groupInFiltered = filteredAvailable.find((p) => p.classOrClassGroupUuid === group.classOrClassGroupUuid);
        return !groupInFiltered
          ? group
          : {
              ...group,
              members: group.members.map((member) => {
                const memberInFiltered = groupInFiltered.members.find((m) => m.uuid === member.uuid);
                return !memberInFiltered ? member : { ...member, selected: !allAvailableSelected };
              }),
            };
      }),
    );
  }

  function handleToggleAvailableClassOrClassGroup(classOrClassGroupUuid: string, allSelected: boolean) {
    setAvailable((prev) =>
      prev.map((group) => ({
        ...group,
        members: group.members.map((member) => {
          const memberInFiltered = filteredAvailable
            .find((p) => p.classOrClassGroupUuid === classOrClassGroupUuid)
            ?.members.find((m) => m.uuid === member.uuid);

          return !memberInFiltered ? member : { ...member, selected: !allSelected };
        }),
      })),
    );
  }

  function handleToggleAvailable(profileUuid: string) {
    setAvailable((prev) =>
      prev.map((group) => ({
        ...group,
        members: group.members.map((member) =>
          member.uuid === profileUuid ? { ...member, selected: !member.selected } : member,
        ),
      })),
    );
  }

  function handleToggleAvailableCollapse(uuid: string) {
    setAvailable((prev) =>
      prev.map((group) => (group.classOrClassGroupUuid === uuid ? { ...group, collapsed: !group.collapsed } : group)),
    );
  }

  function handleTransferAvailables() {
    // find selected members
    const selectedProfiles: SelectableMember[] = [];
    const includeFromSelectedUuids: string[] = [];

    available.forEach((group) => {
      group.members.forEach((member) => {
        if (member.selected) {
          selectedProfiles.push(member);
          includeFromSelectedUuids.push(group.classOrClassGroupUuid);
        }
      });
    });

    if (selectedProfiles.length === 0) return;

    // remove selected members from available profiles on on the left side
    const newAvailable = available
      .map((group) => ({
        ...group,
        members: group.members
          .filter((member) => {
            return !selectedProfiles.map((p) => p.uuid).includes(member.uuid);
          })
          .map((m) => ({ ...m, selected: false })),
        collapsed: true,
      }))
      .sort((a, b) => naturalStringSort(a.name, b.name))
      .filter((group) => group.members.length > 0);

    // add selected to picked profiles on the right side
    const newPicked = picked.map((group) => {
      const oldMembers = picked.find((a) => a.classOrClassGroupUuid === group.classOrClassGroupUuid)?.members ?? [];
      const uuid = group.classOrClassGroupUuid;
      const role = uuid.includes('students')
        ? 'STUDENT'
        : uuid.includes('teachers')
          ? 'TEACHER'
          : uuid.includes('parents')
            ? 'PARENT'
            : uuid.includes('others')
              ? 'OTHER'
              : '';
      const newMembers = group.classOrClassGroupUuid.startsWith('!')
        ? selectedProfiles.filter((p) => p.organizationRoles.includes(role))
        : selectedProfiles.filter(
            (p) =>
              classes
                .find((c) => c.uuid === group.classOrClassGroupUuid)
                ?.membersConnection.edges.map((e) => e.node.uuid)
                .includes(p.uuid) ||
              classGroups
                .find((cg) => cg.uuid === group.classOrClassGroupUuid)
                ?.membersConnection.edges.map((e) => e.node.uuid)
                .includes(p.uuid),
          );
      const uniqueMembers = uniqueByUuid([...oldMembers, ...newMembers])
        .map((m) => ({ ...m, selected: false }))
        .sort((a, b) => naturalStringSort(a.name, b.name));
      return {
        ...group,
        members: uniqueMembers,
        collapsed: true,
      };
    });

    setAvailable(newAvailable);
    setPicked(newPicked);

    onPick(uniqueByUuid(newPicked.flatMap((g) => g.members)));
  }

  // PICKED PROFILES
  const filteredPicked = picked
    // apply search
    .map((group) => ({
      ...group,
      members: group.members.filter((member) => member.name.toLowerCase().includes(pickedSearch)),
    }))
    // sort, ignore 'special' categories
    .sort((a, b) => {
      const aIsCategory = a.classOrClassGroupUuid.startsWith('!');
      const bIsCategory = b.classOrClassGroupUuid.startsWith('!');
      if (aIsCategory && !bIsCategory) return 1;
      if (!aIsCategory && bIsCategory) return -1;
      return naturalStringSort(a.name, b.name);
    })
    // remove empty classes/classGroups
    .filter((g) => g.members.length > 0);

  const pickedCount = uniqueByUuid(filteredPicked.flatMap((s) => s.members)).length;
  const pickedSelectedCount = uniqueByUuid(filteredPicked.flatMap((p) => p.members.filter((m) => m.selected))).length;
  const allPickedSelected = pickedCount > 0 && pickedCount === pickedSelectedCount;

  function handleToggleAllPicked() {
    setPicked((prev) =>
      prev.map((group) => {
        const groupInFiltered = filteredPicked.find((p) => p.classOrClassGroupUuid === group.classOrClassGroupUuid);
        return !groupInFiltered
          ? group
          : {
              ...group,
              members: group.members.map((member) => {
                const memberInFiltered = groupInFiltered.members.find((m) => m.uuid === member.uuid);
                return !memberInFiltered ? member : { ...member, selected: !allPickedSelected };
              }),
            };
      }),
    );
  }

  function handleTogglePickedClassOrClassGroup(classOrClassGroupUuid: string, allSelected: boolean) {
    setPicked((prev) =>
      prev.map((group) => ({
        ...group,
        members: group.members.map((member) => {
          const memberInFiltered = filteredPicked
            .find((p) => p.classOrClassGroupUuid === classOrClassGroupUuid)
            ?.members.find((m) => m.uuid === member.uuid);

          return !memberInFiltered ? member : { ...member, selected: !allSelected };
        }),
      })),
    );
  }

  function handleTogglePicked(pickedUuid: string) {
    setPicked((prev) =>
      prev.map((group) => ({
        ...group,
        members: group.members.map((member) =>
          member.uuid === pickedUuid ? { ...member, selected: !member.selected } : member,
        ),
      })),
    );
  }

  function handlePickedToggleCollapse(uuid: string) {
    setPicked((prev) =>
      prev.map((group) => (group.classOrClassGroupUuid === uuid ? { ...group, collapsed: !group.collapsed } : group)),
    );
  }

  function handleTransferPicked() {
    // find selected members
    const selectedProfiles: SelectableMember[] = [];
    const includeFromSelectedUuids: string[] = [];

    picked.forEach((group) => {
      group.members.forEach((member) => {
        if (member.selected) {
          selectedProfiles.push(member);
          includeFromSelectedUuids.push(group.classOrClassGroupUuid);
        }
      });
    });

    if (selectedProfiles.length === 0) return;

    // remove selected members from picked profiles on the right side
    const newPicked = picked.map((group) => ({
      ...group,
      members: group.members
        .filter((member) => !selectedProfiles.map((p) => p.uuid).includes(member.uuid))
        .map((m) => ({ ...m, selected: false })),
      collapsed: true,
    }));

    // add selected to available profiles on the left side
    const newAvailable = available.map((group) => {
      const oldMembers = available.find((a) => a.classOrClassGroupUuid === group.classOrClassGroupUuid)?.members ?? [];
      const uuid = group.classOrClassGroupUuid;
      const role = uuid.includes('students')
        ? 'STUDENT'
        : uuid.includes('teachers')
          ? 'TEACHER'
          : uuid.includes('parents')
            ? 'PARENT'
            : uuid.includes('others')
              ? 'OTHER'
              : '';
      const newMembers = group.classOrClassGroupUuid.startsWith('!')
        ? selectedProfiles.filter((p) => p.organizationRoles.includes(role))
        : selectedProfiles.filter(
            (p) =>
              classes
                .find((c) => c.uuid === group.classOrClassGroupUuid)
                ?.membersConnection.edges.map((e) => e.node.uuid)
                .includes(p.uuid) ||
              classGroups
                .find((cg) => cg.uuid === group.classOrClassGroupUuid)
                ?.membersConnection.edges.map((e) => e.node.uuid)
                .includes(p.uuid),
          );
      const uniqueMembers = uniqueByUuid([...oldMembers, ...newMembers])
        .map((m) => ({ ...m, selected: false }))
        .sort((a, b) => naturalStringSort(a.name, b.name));
      return {
        ...group,
        members: uniqueMembers,
        collapsed: true,
      };
    });

    setAvailable(newAvailable);
    setPicked(newPicked);

    onPick(uniqueByUuid(newPicked.flatMap((g) => g.members)));
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: infinite loop
  useEffect(() => {
    // construct initial availableModel
    const availableModel: GroupedProfile[] = [];

    classes.forEach((c) => {
      if (categoriesOnly) return;
      availableModel.push({
        classOrClassGroupUuid: c.uuid,
        name: c.name,
        collapsed: true,
        members:
          profiles
            .filter(
              (p) =>
                !uniqueMembers.map((m) => m.uuid).includes(p.uuid) &&
                c.membersConnection.edges.some((e) => e.node.uuid === p.uuid) &&
                !excludeMemberUuids.includes(p.uuid),
            )
            .map((p) => ({
              ...p,
              name: p.selectName ?? '',
              selected: false,
            })) ?? [],
      });
    });

    classGroups.forEach((cg) => {
      if (categoriesOnly) return;
      const c = classes.find((c) => c.divisionsConnection.edges.some((e) => e.node.uuid === cg.division.uuid));
      availableModel.push({
        classOrClassGroupUuid: cg.uuid,
        name: `${c?.name ?? ''} (${cg.name})`,
        collapsed: true,
        members:
          profiles
            .filter(
              (p) =>
                !uniqueMembers.map((m) => m.uuid).includes(p.uuid) &&
                cg.membersConnection.edges.some((e) => e.node.uuid === p.uuid) &&
                !excludeMemberUuids.includes(p.uuid),
            )
            .map((p) => ({
              ...p,
              name: p.selectName ?? '',
              selected: false,
            })) ?? [],
      });
    });

    // add extra categories for available profiles (group by students, teachers, parents or others)
    // extra categories start with '!' so that they can be identified for sorting, filtering or grouping later
    availableModel.push({
      classOrClassGroupUuid: '!available-students',
      name: t('rolesInOrganization.student', { count: 2 }),
      collapsed: true,
      members:
        students
          .filter((s) => !uniqueMembers.map((m) => m.uuid).includes(s.uuid) && !excludeMemberUuids.includes(s.uuid))
          .map((s) => ({
            ...s,
            name: s.selectName ?? '',
            selected: false,
          }))
          .sort((a, b) => naturalStringSort(a.name, b.name)) ?? [],
    });

    availableModel.push({
      classOrClassGroupUuid: '!available-teachers',
      name: t('rolesInOrganization.teacher', { count: 2 }),
      collapsed: true,
      members:
        teachers
          .filter((t) => !uniqueMembers.map((m) => m.uuid).includes(t.uuid) && !excludeMemberUuids.includes(t.uuid))
          .map((t) => ({
            ...t,
            name: t.selectName ?? '',
            selected: false,
          }))
          .sort((a, b) => naturalStringSort(a.name, b.name)) ?? [],
    });

    availableModel.push({
      classOrClassGroupUuid: '!available-parents',
      name: t('rolesInOrganization.parent', { count: 2 }),
      collapsed: true,
      members:
        parents
          .filter((p) => !uniqueMembers.map((m) => m.uuid).includes(p.uuid) && !excludeMemberUuids.includes(p.uuid))
          .map((p) => ({
            ...p,
            name: p.selectName ?? '',
            selected: false,
          }))
          .sort((a, b) => naturalStringSort(a.name, b.name)) ?? [],
    });

    availableModel.push({
      classOrClassGroupUuid: '!available-others',
      name: t('rolesInOrganization.other', { count: 2 }),
      collapsed: true,
      members:
        others
          .filter((o) => !uniqueMembers.map((m) => m.uuid).includes(o.uuid) && !excludeMemberUuids.includes(o.uuid))
          .map((o) => ({
            ...o,
            name: o.selectName ?? '',
            selected: false,
          }))
          .sort((a, b) => naturalStringSort(a.name, b.name)) ?? [],
    });

    // construct initial pickedModel
    const pickedModel: GroupedProfile[] = [];

    // Only add classes that are included in limitPickedToUuids
    classes.forEach((c) => {
      if (categoriesOnly) return;
      // if (!limitPickedToUuids.includes(c.uuid)) return;
      pickedModel.push({
        classOrClassGroupUuid: c.uuid,
        name: c.name,
        collapsed: true,
        members:
          uniqueMembers.filter(
            (p) =>
              c.membersConnection.edges.some((e) => e.node.uuid === p.uuid) && !excludeMemberUuids.includes(p.uuid),
          ) ?? [],
      });
    });

    // Only add classGroups that are included in limitPickedToUuids
    classGroups.forEach((cg) => {
      if (categoriesOnly) return;
      // if (!limitPickedToUuids.includes(cg.uuid)) return;
      const c = classes.find((c) => c.divisionsConnection.edges.some((e) => e.node.uuid === cg.division.uuid));
      pickedModel.push({
        classOrClassGroupUuid: cg.uuid,
        name: `${c?.name ?? ''} (${cg.name})`,
        collapsed: true,
        members:
          uniqueMembers.filter(
            (p) =>
              cg.membersConnection.edges.some((e) => e.node.uuid === p.uuid) && !excludeMemberUuids.includes(p.uuid),
          ) ?? [],
      });
    });

    // add extra categories for picked profiles (group by students, teachers, parents or others)
    // extra categories start with '!' so that they can be identified for sorting, filtering or grouping later
    pickedModel.push({
      classOrClassGroupUuid: '!picked-students',
      name: t('rolesInOrganization.student', { count: 2 }),
      collapsed: true,
      members: uniqueMembers
        .filter((m) => m.organizationRoles.includes('STUDENT') && !excludeMemberUuids.includes(m.uuid))
        .sort((a, b) => naturalStringSort(a.name, b.name)),
    });

    pickedModel.push({
      classOrClassGroupUuid: '!picked-teachers',
      name: t('rolesInOrganization.teacher', { count: 2 }),
      collapsed: true,
      members: uniqueMembers
        .filter((m) => m.organizationRoles.includes('TEACHER') && !excludeMemberUuids.includes(m.uuid))
        .sort((a, b) => naturalStringSort(a.name, b.name)),
    });

    pickedModel.push({
      classOrClassGroupUuid: '!picked-parents',
      name: t('rolesInOrganization.parent', { count: 2 }),
      collapsed: true,
      members: uniqueMembers
        .filter((m) => m.organizationRoles.includes('PARENT') && !excludeMemberUuids.includes(m.uuid))
        .sort((a, b) => naturalStringSort(a.name, b.name)),
    });

    pickedModel.push({
      classOrClassGroupUuid: '!picked-others',
      name: t('rolesInOrganization.other', { count: 2 }),
      collapsed: true,
      members: uniqueMembers
        .filter((m) => m.organizationRoles.includes('OTHER') && !excludeMemberUuids.includes(m.uuid))
        .sort((a, b) => naturalStringSort(a.name, b.name)),
    });

    setAvailable(availableModel);
    setPicked(pickedModel);
  }, [initialMembers]);

  return (
    <div className={styles['profile-selection']}>
      <Grid className={styles.grid}>
        <GridRow className={styles.row}>
          <GridColumn width={4} fitRowHeight>
            <div className={styles.headline}>{t('profiles.profilesForSelection')}</div>
            <SearchInput
              name='available-search'
              onChange={(e) => setAvailableSearch(e.target.value.toLowerCase())}
              value={availableSearch}
              isOnWhite
              className={styles.search}
            />
            <div className={styles.header}>
              <Checkbox
                name='available-all'
                label={t('common.selectAll')}
                checked={allAvailableSelected}
                onChange={() => handleToggleAvailableAll()}
              />
              <div className={styles.count}>
                ({availableSelectedCount}/{availableCount})
              </div>
            </div>
            <div className={classNames(styles.scroll, 'has-scrollbar')}>
              {filteredAvailable.length === 0 ? (
                <EmptyState padding='l' size='small' icon={<ProfileIcon />} subtitle={t('persons.noPerson')} />
              ) : (
                filteredAvailable.map((group, index) => {
                  const groupSelectedCount = group.members.map((m) => m.selected).filter((selected) => selected).length;
                  const groupMembersCount = group.members.length;
                  const allMembersSelected = groupMembersCount === groupSelectedCount;

                  const showDivider =
                    filteredAvailable.filter((g) => !g.classOrClassGroupUuid.startsWith('!')).length > 0 &&
                    filteredAvailable.findIndex((g) => g.classOrClassGroupUuid.startsWith('!')) === index;

                  return (
                    <div
                      className={classNames(styles.group, {
                        [styles.collapsed]: group.collapsed,
                        [styles.divider]: showDivider,
                      })}
                      key={`available-group-${group.classOrClassGroupUuid}`}
                    >
                      <div
                        className={styles['group-header']}
                        onClick={() =>
                          group.members.length > 0 && handleToggleAvailableCollapse(group.classOrClassGroupUuid)
                        }
                        title={group.name}
                      >
                        <ArrowDownIcon className={styles.icon} />
                        <Checkbox
                          name={`${context}-available-all-${group.classOrClassGroupUuid}`}
                          key={`${context}-available-all-group-${group.classOrClassGroupUuid}`}
                          checked={allMembersSelected}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleAvailableClassOrClassGroup(group.classOrClassGroupUuid, allMembersSelected);
                          }}
                        />
                        <div className={styles['group-name']}>{group.name}</div>
                        <div className={styles.count}>
                          ({groupSelectedCount}/{groupMembersCount})
                        </div>
                      </div>
                      <div className={styles['group-body']}>
                        {group.members.map((m) => {
                          return (
                            <Checkbox
                              className={styles.member}
                              name={`${context}-available-member-${group.classOrClassGroupUuid}-${m.uuid}`}
                              key={`${context}-available-member-${group.classOrClassGroupUuid}-${m.uuid}`}
                              label={m.name}
                              checked={m.selected}
                              onChange={() => handleToggleAvailable(m.uuid)}
                              preventLabelWrapping
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </GridColumn>

          <GridColumn width={4} className={styles['no-scroll']} align='center'>
            <div className={styles.buttons}>
              <Button
                hierarchy='secondary'
                icon={<ArrowRightIcon />}
                iconPosition='right'
                onClick={() => handleTransferAvailables()}
              >
                {t('common.applySelection')}
              </Button>
              <Button hierarchy='secondary' icon={<ArrowLeftIcon />} onClick={() => handleTransferPicked()}>
                {t('common.applySelection')}
              </Button>
            </div>
          </GridColumn>

          <GridColumn width={4} fitRowHeight>
            <div className={styles.headline}>{t('profiles.picked')}</div>
            <SearchInput
              name='picked-search'
              onChange={(e) => setPickedSearch(e.target.value.toLowerCase())}
              value={pickedSearch}
              isOnWhite
              className={styles.search}
            />
            <div className={styles.header}>
              <Checkbox
                name='picked-all'
                label={t('common.selectAll')}
                checked={allPickedSelected}
                onChange={() => handleToggleAllPicked()}
              />
              <div className={styles.count}>
                ({pickedSelectedCount}/{pickedCount})
              </div>
            </div>
            <div className={classNames(styles.scroll, 'has-scrollbar')}>
              {filteredPicked.length === 0 ? (
                <EmptyState padding='l' size='small' icon={<ProfileIcon />} subtitle={t('persons.noPerson')} />
              ) : (
                filteredPicked.map((group, index) => {
                  const groupSelectedCount = group.members.map((m) => m.selected).filter((selected) => selected).length;
                  const groupMembersCount = group.members.length;
                  const allMembersSelected = groupMembersCount > 0 && groupSelectedCount === groupMembersCount;

                  const showDivider =
                    filteredPicked.filter((g) => !g.classOrClassGroupUuid.startsWith('!')).length > 0 &&
                    filteredPicked.findIndex((g) => g.classOrClassGroupUuid.startsWith('!')) === index;

                  return (
                    <div
                      className={classNames(styles.group, {
                        [styles.collapsed]: group.collapsed,
                        [styles.divider]: showDivider,
                      })}
                      key={`picked-group-${group.classOrClassGroupUuid}`}
                    >
                      <div
                        className={styles['group-header']}
                        onClick={() =>
                          group.members.length > 0 && handlePickedToggleCollapse(group.classOrClassGroupUuid)
                        }
                        title={group.name}
                      >
                        <ArrowDownIcon className={styles.icon} />
                        <Checkbox
                          name={`${context}-picked-all-${group.classOrClassGroupUuid}`}
                          key={`${context}-picked-all-group-${group.classOrClassGroupUuid}`}
                          checked={allMembersSelected}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePickedClassOrClassGroup(group.classOrClassGroupUuid, allMembersSelected);
                          }}
                        />
                        <div className={styles['group-name']}>{group.name}</div>
                        <div className={styles.count}>
                          ({groupSelectedCount}/{groupMembersCount})
                        </div>
                      </div>
                      <div className={styles['group-body']}>
                        {group.members.map((m) => {
                          return (
                            <Checkbox
                              className={styles.member}
                              name={`${context}-picked-member-${group.classOrClassGroupUuid}-${m.uuid}`}
                              key={`${context}-picked-member-${group.classOrClassGroupUuid}-${m.uuid}`}
                              label={m.name}
                              checked={m.selected}
                              onChange={() => handleTogglePicked(m.uuid)}
                              preventLabelWrapping
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </GridColumn>
        </GridRow>
      </Grid>
    </div>
  );
};
